// 간편접수 「간단/상세」 모드 — 모드 해석·상세 칸 이름·서버 재검증(화면·액션·인메모리·Supabase 가 한 벌을 쓴다).

import { z } from "zod";

import {
  BUDGET_BANDS,
  CALL_TIMES,
  CustomerIntakeDetail,
  WORK_SCOPES,
} from "./types";
import { ORG_SETTING_DEFAULT } from "../org/types";

export const INTAKE_MODES = ["simple", "detail"] as const;
export type IntakeMode = (typeof INTAKE_MODES)[number];

export const INTAKE_MODE_LABEL: Record<IntakeMode, string> = {
  simple: "간단",
  detail: "상세",
};

// 상세 칸 이름 — 폼 name · 고객 열 · RPC p_payload 키가 같다.
export const INTAKE_DETAIL_FIELDS = ["work_scope", "budget_band", "call_time"] as const;

export const EMPTY_INTAKE_DETAIL: CustomerIntakeDetail = {
  work_scope: null,
  budget_band: null,
  call_time: null,
};

// 모르는 값·빈 값은 기본(간단)으로 본다.
export const intakeModeOf = (value: string | null | undefined): IntakeMode =>
  value === "detail" ? "detail" : (ORG_SETTING_DEFAULT["intake.mode"] as IntakeMode);

const choice = <T extends readonly [string, ...string[]]>(values: T, label: string) =>
  z.preprocess((v) => (v === "" ? null : v), z.enum(values, { message: `${label} 값을 다시 골라 주세요` }).nullable());

const intakeDetailSchema = z.object({
  work_scope: choice(WORK_SCOPES, "공사 종류"),
  budget_band: choice(BUDGET_BANDS, "예산대"),
  call_time: choice(CALL_TIMES, "통화 가능 시간"),
});

export type IntakeDetailResult =
  | { ok: true; value: CustomerIntakeDetail }
  | { ok: false; message: string };

// 간단 모드면 무엇이 와도 버리고, 상세 모드면 목록 밖 값을 거절한다.
export const readIntakeDetail = (
  mode: IntakeMode,
  raw: Partial<Record<(typeof INTAKE_DETAIL_FIELDS)[number], string>>
): IntakeDetailResult => {
  if (mode !== "detail") return { ok: true, value: { ...EMPTY_INTAKE_DETAIL } };
  const parsed = intakeDetailSchema.safeParse({
    work_scope: raw.work_scope ?? "",
    budget_band: raw.budget_band ?? "",
    call_time: raw.call_time ?? "",
  });
  if (!parsed.success) return { ok: false, message: parsed.error.issues[0]?.message ?? "입력을 확인해 주세요" };
  return { ok: true, value: parsed.data };
};
